import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAccountStorage, purgeAccountOrphans, purgeAccountRecycleBin } from '../api/account';
import { useConfirm } from './ConfirmDialog';
import { drainDelete } from './storageDrain';
import { splitProjectsByKind, qualityCheckOrigin } from './storageSections';
import { isNewTabClick } from '../utils/navigation';
import './StorageUsageView.css';

// Bytes → readable size (extends FileRepository's formatSize with a GB tier;
// storage totals can run into GBs). Kept local — the repo duplicates small
// helpers like this rather than sharing them (a known refactor).
function formatSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// Collapsible section header. Mirrors WorkflowPanel's CollapsibleGroup (caret +
// role=button + Enter/Space); replicated rather than imported because that one is
// module-local to the panels tree. `action` renders BESIDE the label — the trailing
// size span takes the `margin-left: auto`, so a button never floats alone at the
// far edge of an otherwise empty row.
function SectionHeader({ label, open, onToggle, action, trailing }) {
  return (
    <h4
      className="storage-section-toggle"
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <span className="storage-caret">{open ? '▾' : '▸'}</span>
      {label}
      {action}
      {trailing}
    </h4>
  );
}

/**
 * One project row + its per-tile breakdown when expanded. Shared by the Projects and
 * Quality Check sections — the two differ in which rows they list and what the header
 * says, not in how a row looks. `expanded` stays keyed by project id in the parent's
 * one map, so a row discloses the same way in either section.
 */
function ProjectRow({ project: p, expanded, onToggle, onLeaving }) {
  const origin = qualityCheckOrigin(p.qualityCheck);
  return (
    <div className="storage-row-group">
      <div className="storage-row storage-row-project" onClick={onToggle}>
        <span className="storage-caret">{expanded ? '▾' : '▸'}</span>
        <span className="storage-name">{p.name || <em>(orphaned — no project record)</em>}</span>
        {/* Which project a check run was captured from — the one thing the section
            header can't say. Absent for an ordinary project. */}
        {origin && <span className="storage-origin">from {origin}</span>}
        {/* A link, so the project can be ctrl/⌘+clicked into a new tab and this
            inventory kept open beside it. `onLeaving` closes the host popup, and is
            deliberately SKIPPED for a new-tab gesture: shutting the popup when the
            user has not gone anywhere is the opposite of what they asked for. */}
        <Link
          className="applink storage-open"
          to={`/project/${p.id}`}
          title="Open project"
          onClick={(e) => {
            e.stopPropagation();
            if (!isNewTabClick(e)) onLeaving?.();
          }}
        >
          Open ↗
        </Link>
        <span className="storage-bytes">{formatSize(p.bytes)}</span>
      </div>
      {expanded && (
        <div className="storage-tiles">
          {p.tiles.map((t) => (
            <div className="storage-row storage-row-tile" key={t.nodeId}>
              <span className="storage-tile-label">{t.label}</span>
              {t.type && <span className="storage-tile-type">{t.type}</span>}
              <span className="storage-bytes">{formatSize(t.bytes)}</span>
            </div>
          ))}
          {p.unattributedBytes > 0 && (
            <div className="storage-row storage-row-tile">
              <span className="storage-tile-label storage-muted">Other / unassigned</span>
              <span className="storage-bytes">{formatSize(p.unattributedBytes)}</span>
            </div>
          )}
          {p.tiles.length === 0 && p.unattributedBytes === 0 && (
            <div className="storage-row storage-row-tile storage-muted">No stored artifacts.</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Account storage inventory body: what the account is storing, grouped project →
 * tile, so the user can see what's taking space and open a project to delete it.
 * Self-fetching (on mount). Rendered inside the "Memory" tab of the process
 * monitor popup (ProcessMonitorButton). `onNavigate` (optional) lets the host close
 * its popup as a row's project link is followed — the link itself does the routing,
 * and a new-tab gesture skips the callback, since nothing is being navigated away
 * from.
 */
export default function StorageUsageView({ onNavigate }) {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // projectId -> bool
  const [openSections, setOpenSections] = useState({}); // section key -> bool
  const [busy, setBusy] = useState(null); // 'orphans' | 'recycle' | null
  const [progress, setProgress] = useState(null);

  const reload = () => {
    setLoading(true);
    setError(null);
    return getAccountStorage()
      .then((d) => setData(d))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getAccountStorage()
      .then((d) => {
        if (active) setData(d);
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Shared driver for both purges: they are resumable, so loop until the backend
  // says done and only then report success.
  async function runPurge(kind, request, verb) {
    setBusy(kind);
    setError(null);
    setProgress(null);
    try {
      const totals = await drainDelete(request, (p) =>
        setProgress(`${verb} ${p.objects} object(s), ${formatSize(p.bytes)} freed…`)
      );
      if (!totals.done) {
        setError(
          `Partially freed ${formatSize(totals.bytes)} — there is more to remove. Run it again.`
        );
      }
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
      setProgress(null);
    }
  }

  async function handlePurge() {
    const o = data?.orphans;
    if (!o) return;
    const nFolders = o.s3.length;
    const nRecords = o.dynamo.length;
    const msg =
      `Permanently delete ${nFolders} orphaned folder(s) (${formatSize(o.bytes)})` +
      (nRecords ? ` and ${nRecords} stale record(s)` : '') +
      `? This frees the space immediately. There is no backup — it cannot be undone.`;
    if (
      !(await confirm({
        title: 'Delete orphaned storage?',
        body: msg,
        danger: true,
        confirmText: 'Delete forever',
      }))
    )
      return;
    await runPurge('orphans', purgeAccountOrphans, 'Purged');
  }

  async function handleEmptyRecycleBin() {
    const r = data?.recoverableDeleted;
    if (!r) return;
    const msg =
      `Permanently delete ${r.objectCount} retained object(s), freeing ${formatSize(r.bytes)}? ` +
      `This also removes older versions of files that still exist, so you lose the ability ` +
      `to roll back an overwrite. There is no backup — it cannot be undone.`;
    if (
      !(await confirm({
        title: 'Empty recently deleted?',
        body: msg,
        danger: true,
        confirmText: 'Delete forever',
      }))
    )
      return;
    await runPurge('recycle', purgeAccountRecycleBin, 'Deleted');
  }

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Two sections out of the one `projects` array (the backend tags each row's `kind`),
  // so the Projects count means the user's OWN projects.
  const { projects, qualityChecks } = splitProjectsByKind(data?.projects);

  // Every section header renders whether or not it has rows — the five are a fixed
  // vocabulary, and hiding an empty one made the view's shape depend on the data, so
  // "nothing to reclaim" and "no such category" looked identical (the DashboardSidebar
  // rail settled this the same way). The two IRREVERSIBLE actions stay gated on there
  // being something to act on: a Purge button whose only possible outcome is "delete 0
  // objects" is a hazard, not a feature.
  const hasOrphans = !!(data?.orphans?.s3?.length || data?.orphans?.dynamo?.length);
  const retainedCount = data?.recoverableDeleted?.objectCount || 0;

  return (
    <div className="storage-view">
      {loading && <p className="storage-muted">Loading…</p>}
      {error && <p className="storage-error">{error}</p>}
      {progress && <p className="storage-hint">{progress}</p>}

      {data && !loading && (
        <>
          <div className="storage-view-total">{formatSize(data.totalBytes)} in use</div>
          <p className="storage-hint">
            What your account is storing, by project and module. Open a project to delete a module
            or the whole project and free its space.
          </p>

          {/* ── Projects → tiles ── */}
          <section className="storage-section">
            <SectionHeader
              label="Projects"
              open={openSections.projects}
              onToggle={() => toggleSection('projects')}
              trailing={<span className="storage-count">{projects.length}</span>}
            />
            {openSections.projects && (
              <>
                {projects.length === 0 && (
                  <p className="storage-muted">No project artifacts stored.</p>
                )}
                {projects.map((p) => (
                  <ProjectRow
                    key={p.id}
                    project={p}
                    expanded={!!expanded[p.id]}
                    onToggle={() => toggle(p.id)}
                    onLeaving={onNavigate}
                  />
                ))}
              </>
            )}
          </section>

          {/* ── Quality Check runs → tiles ── Their own section, not rows inside
              Projects: a check run IS a real project row (hidden from the dashboard
              by `system: true`), so listing them together made the Projects count a
              number the user had to discount, and read as projects they never made. */}
          <section className="storage-section">
            <SectionHeader
              label="Quality Check"
              open={openSections.qualityCheck}
              onToggle={() => toggleSection('qualityCheck')}
              trailing={<span className="storage-count">{qualityChecks.length}</span>}
            />
            {openSections.qualityCheck && (
              <>
                <p className="storage-hint">
                  One project per run of a registered check. They are removed when you delete the
                  check on the Quality Check page.
                </p>
                {qualityChecks.length === 0 && (
                  <p className="storage-muted">No Quality Check runs stored.</p>
                )}
                {qualityChecks.map((p) => (
                  <ProjectRow
                    key={p.id}
                    project={p}
                    expanded={!!expanded[p.id]}
                    onToggle={() => toggle(p.id)}
                    onLeaving={onNavigate}
                  />
                ))}
              </>
            )}
          </section>

          {/* ── Repository files ── */}
          <section className="storage-section">
            <SectionHeader
              label="Repository files"
              open={openSections.files}
              onToggle={() => toggleSection('files')}
              trailing={<span className="storage-count">{data.files.length}</span>}
            />
            {openSections.files && (
              <>
                {data.files.length === 0 && (
                  <p className="storage-muted">No repository files stored.</p>
                )}
                {data.files.map((f) => (
                  <div className="storage-row" key={f.id}>
                    <span className="storage-name">{f.name}</span>
                    <span className="storage-bytes">{formatSize(f.bytes)}</span>
                  </div>
                ))}
              </>
            )}
          </section>

          {/* ── Orphans / reclaimable ── The amber warn styling is applied only when
              there IS leftover data: a permanent warning box reporting 0 B trains the
              user to ignore it. */}
          <section className={`storage-section${hasOrphans ? ' storage-section-warn' : ''}`}>
            <SectionHeader
              label="Leftover data"
              open={openSections.orphans}
              onToggle={() => toggleSection('orphans')}
              action={
                hasOrphans && (
                  <button
                    className="storage-purge"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePurge();
                    }}
                    disabled={!!busy}
                    title="Permanently delete all leftover data"
                  >
                    {busy === 'orphans' ? 'Purging…' : 'Purge all'}
                  </button>
                )
              }
              trailing={<span className="storage-bytes">{formatSize(data.orphans.bytes)}</span>}
            />
            {openSections.orphans && (
              <>
                <p className="storage-hint">
                  Data with no matching project — likely from deletes that didn&apos;t fully clean
                  up. Purging frees the space permanently; it cannot be undone.
                </p>
                {!hasOrphans && <p className="storage-muted">Nothing to reclaim.</p>}
                {data.orphans.s3.map((o) => (
                  <div className="storage-row" key={o.key}>
                    <span className="storage-name storage-mono">{o.projectId}</span>
                    <span className="storage-bytes">{formatSize(o.bytes)}</span>
                  </div>
                ))}
                {data.orphans.dynamo.length > 0 && (
                  <p className="storage-muted">+ {data.orphans.dynamo.length} stale record(s)</p>
                )}
              </>
            )}
          </section>

          {/* ── Recently deleted (still retained, still billed) ── */}
          <section className="storage-section">
            <SectionHeader
              label="Recently deleted"
              open={openSections.recycle}
              onToggle={() => toggleSection('recycle')}
              action={
                // Hidden when the versions listing degraded: no s3:ListBucketVersions
                // means no s3:DeleteObjectVersion either, so the delete would fail.
                retainedCount > 0 &&
                data.recoverableDeleted.available !== false && (
                  <button
                    className="storage-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEmptyRecycleBin();
                    }}
                    disabled={!!busy}
                    title="Permanently delete all retained versions"
                  >
                    {busy === 'recycle' ? 'Emptying…' : 'Empty'}
                  </button>
                )
              }
              trailing={
                <span className="storage-bytes">{formatSize(data.recoverableDeleted.bytes)}</span>
              }
            />
            {openSections.recycle &&
              (retainedCount > 0 ? (
                <p className="storage-hint">
                  {retainedCount} deleted object(s) still retained — and still taking up space — for{' '}
                  {data.recoverableDeleted.retentionDays} days, then freed automatically. Empty to
                  free it now; this also drops older versions of files that still exist, and cannot
                  be undone.
                </p>
              ) : (
                <p className="storage-muted">
                  Nothing retained. A deleted object stays here — and still takes up space — for{' '}
                  {data.recoverableDeleted.retentionDays} days, then is freed automatically.
                </p>
              ))}
          </section>
        </>
      )}
    </div>
  );
}
